import React, { FunctionComponent, useState } from 'react'
import { Button, Col, Row, Dialog } from '@paypalcorp/pp-react'
import withAnalytics from '../../../utils/withAnalytics'
import { removeGAParam } from '../../../components/pp-react-wrappers'

import styles from '../integration-builder.module.css'

const HelpModal: FunctionComponent = () => {
  const [isOpen, setIsOpen] = useState(false)
  const fptiData = typeof window !== `undefined` && window.fpti

  const recordAnalytics = () => {
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      const eventNameSuffix = isOpen ? 'closed' : 'shown'

      window.PAYPAL.analytics.instance.record({
        data: {
          ...fptiData,
          component: 'devdiscoverynodeweb',
          space_key: 'SKEXJE',
          event_name: `devexp_${window.fpti.page.replace(
            /[-:]/g,
            '_',
          )}_help_modal_${eventNameSuffix}`,
          e: 'cl',
          link: 'help',
          event_action: 'help',
          page_name: fptiData.page.replace(/:/g, '_'),
          pu: removeGAParam(window.location.href),
        },
      })
    }
  }

  return (
    <>
      <Button
        secondary
        onClick={() => {
          setIsOpen(true)
          recordAnalytics()
        }}
      >
        Help/FAQs
      </Button>
      <Dialog
        title="Help/FAQs"
        id="helpModal"
        isOpen={isOpen}
        closeOnBackgroundClick
        skipFormFocus
        onClose={() => {
          setIsOpen(false)
          recordAnalytics()
        }}
      >
        <Col>
          <Row>
            <h4>
              <b>What is Integration Builder?</b>
            </h4>
            <p>
              Integration Builder is a tool that allows you to choose the
              payment options you want, customize your button layout, and
              preview your checkout experience. Once you've configured your
              experience, you can use the provided front-end and back-end code
              files to add the PayPal checkout experience to your website.
            </p>
          </Row>
          <Row>
            <h4>
              <b>
                How does Integration Builder help me with checkout payment
                solutions?
              </b>
            </h4>
            <p>
              With the Integration Builder, you can choose your payment options,
              adjust the layout, and see a preview of the checkout experience.
              Instead of coding the integration, you can download the provided
              code files and add them to your website's code.
            </p>
          </Row>
          <Row>
            <h4>
              <b>Is the Integration Builder a no-code tool?</b>
            </h4>
            <p>
              The Integration Builder is a low-code tool. You can configure the
              PayPal checkout experience from the Integration Builder, but you
              will need to incorporate the provided code files into your website
              code.
            </p>
            <p>
              For a solution that doesn't require coding or a website, check out{' '}
              <a href="https://www.paypal.com/buttons/smart">
                build a pay link and button
              </a>
              .
            </p>
          </Row>
          <Row>
            <h4>
              <b>
                Can I configure or customize the checkout layout for PayPal
                Checkout?
              </b>
            </h4>
            <p>
              Yes. You can choose from either a horizontal or vertical button
              layout.
            </p>
          </Row>
          <Row>
            <h4>
              <b>
                What is the difference between PayPal Checkout vs Expanded
                Checkout?
              </b>
            </h4>
            <p>
              PayPal Checkout is a branded PayPal checkout experience that
              allows you to offer payment via PayPal, Pay Later, and Venmo.
              Users can also checkout using a debit or credit card through a
              PayPal-hosted guest checkout.
            </p>
            <p>
              Expanded Checkout is an unbranded checkout experience that allows
              you to offer payment via PayPal, Pay Later, Venmo, and Alternative
              Payment Methods. Expanded Checkout also offers customizable credit
              and debit form fields. You'll need to use PayPal's Orders API to
              process debit and credit payments directly through your website.
              Expanded Checkout also supports 3D Secure transactions and offers
              fraud protection.
            </p>
          </Row>
          <Row>
            <h4>
              <b>
                What are the different types of accounts required for the
                integration and where do I sign up for them?
              </b>
            </h4>
            <p>
              PayPal Developer Account – Use this account to access the Sandbox
              and Live environments on the Developer Dashboard -
              <a
                href="https://www.paypal.com/signin"
                target="_blank"
                rel="noreferrer"
              >
                https://www.paypal.com/signin
              </a>
              <br />
              Sandbox Accounts – Create dummy accounts in the sandbox
              environment to test transactions –
              <a
                href="https://developer.paypal.com/dashboard/accounts"
                target="_blank"
                rel="noreferrer"
              >
                https://developer.paypal.com/dashboard/accounts
              </a>
              <br />
              PayPal Business Account – A verified business account is required
              to process transactions -
              <a
                href="https://www.paypal.com/us/business/open-business-account"
                target="_blank"
                rel="noreferrer"
              >
                https://www.paypal.com/us/business/open-business-account
              </a>
            </p>
          </Row>
          <Row>
            <h4>
              <b>Which account approval is required to go live?</b>
            </h4>
            <p>
              Your app must be registered, and your business account must be
              verified before going live. See the Go Live checklist for more
              information:{' '}
              <a
                href="https://developer.paypal.com/api/rest/production/"
                target="_blank"
                rel="noreferrer"
              >
                https://developer.paypal.com/api/rest/production/
              </a>
            </p>
          </Row>
          <Row>
            <h4>
              <b>
                Is business account approval mandatory for both PayPal Checkout
                and Expanded Checkout?
              </b>
            </h4>
            <p>
              Yes. You will need a verified business account to process
              transactions through both a PayPal Checkout or Expanded Checkout
              integration.
            </p>
          </Row>
          <Row>
            <h4>
              <b>
                What is the difference between the demo using sample credentials
                and sandbox testing and go-live?
              </b>
            </h4>
            <p>
              The demo using sample credentials uses the default client ID and
              secret to simulate a credit transaction using test card numbers.
              You can see what the transaction flow looks like from the buyer
              and seller perspectives.
            </p>
            <p>
              Sandbox testing allows you to test your checkout integration on
              your website by making transactions using sandbox accounts and
              credentials. You can ensure your checkout integration works as
              intended before using your live credentials.
            </p>
          </Row>
        </Col>
      </Dialog>
    </>
  )
}

export default withAnalytics(HelpModal)
